import { useEffect, useRef, useState } from "react";

import type {
  GetLanguageLabel,
  MarketingBannerRow,
  MarketingBannersTranslations,
  MarketingBannerType,
} from "../types";

import { Button as ButtonV2 } from "../../Button";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import {
  CheckIcon,
  CopyIcon,
  CrossIcon,
  ExpandIcon,
  WorldIcon,
} from "../../Icons";
import { ModalPanel } from "../../ModalPanel";
import { Typo } from "../../Typo";
import { CreativePreview } from "../CreativePreview";

const TYPE_LABEL: Record<MarketingBannerType, string> = {
  html_ad: "HTML",
  image_banner: "Image",
};

const COPIED_RESET_MS = 3000;
const PREVIEW_CONTAINER_PADDING = "5rem";
const PREVIEW_CONTAINER_MIN_WIDTH = 640;

type BannerPreviewDesktopProps = {
  isOpen: boolean;
  onClose: () => void;
  banner: MarketingBannerRow;
  translations: MarketingBannersTranslations;
  getLanguageLabel: GetLanguageLabel;
  /** Fired before the "Copy HTML code" button writes to the clipboard. */
  onCopy?: () => void;
};

export const BannerPreviewDesktop = ({
  isOpen,
  onClose,
  banner,
  translations,
  getLanguageLabel,
  onCopy,
}: BannerPreviewDesktopProps) => {
  const { copied: copiedLabel } = translations.actions;
  const { copyLabel } = translations.actions.getCopyLabels(banner.type);

  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useBodyScrollLock(isOpen);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Reset the "copied" feedback when the modal closes, without a set-state-in-
  // effect: adjust state directly while rendering the isOpen transition (see
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes).
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (!isOpen) setCopied(false);
  }

  // The timer cancellation itself is a real side effect (not a state
  // adjustment), so it stays in an effect rather than the render-time block above.
  useEffect(() => {
    if (!isOpen && timeoutRef.current) clearTimeout(timeoutRef.current);
  }, [isOpen]);

  const handleCopy = async () => {
    onCopy?.();
    try {
      await navigator.clipboard.writeText(banner.creative_code);
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
    } catch (err) {
      console.error("BannerPreviewDesktop: failed to copy creative_code", err);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="banner-preview-modal"
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      <div
        className="absolute inset-0 animate-modal-fade-in bg-black/60"
        onClick={onClose}
        data-testid="banner-preview-modal-overlay"
      />

      <ModalPanel
        data-testid="banner-preview-modal-panel"
        className="relative z-10 mx-4 w-fit max-w-[calc(100vw-2rem)] animate-modal-scale-in rounded-2xl bg-white p-8 shadow-xl lg:mx-0"
        maxHeight="calc(100vh - 2rem)"
      >
        <ModalPanel.Header>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              data-testid="banner-preview-modal-close"
              className="flex h-6 w-6 items-center justify-center"
            >
              <CrossIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="flex flex-col gap-4">
            <Typo type="headingXL">{banner.name}</Typo>
            <div className="flex flex-wrap items-center gap-4">
              <Typo type="bodySM" textColor="text-text-secondary">
                {banner.banner_group}
              </Typo>
              <MetaSeparator />
              <Typo type="bodySM" textColor="text-text-secondary">
                {TYPE_LABEL[banner.type]}
              </Typo>
              <MetaSeparator />
              <span className="flex items-center gap-1">
                <ExpandIcon
                  className="[&_path]:stroke-icon-tertiary"
                  width={16}
                  height={16}
                />
                <Typo type="bodySM" textColor="text-text-secondary">
                  {`${banner.width}×${banner.height}`}
                </Typo>
              </span>
              <MetaSeparator />
              <span className="flex items-center gap-1">
                <WorldIcon
                  width={16}
                  height={16}
                  className="shrink-0 text-text-secondary"
                />
                <Typo type="bodySM" textColor="text-text-secondary">
                  {getLanguageLabel(banner.language.trim() || "en")}
                </Typo>
              </span>
            </div>
          </div>
        </ModalPanel.Header>

        <ModalPanel.Body
          scrollable
          className="items-start justify-center rounded-lg bg-surface-secondary p-10"
          style={{
            flex: "1 1 auto",
            width: `calc(${banner.width}px + ${PREVIEW_CONTAINER_PADDING})`,
            height: `calc(${banner.height}px + ${PREVIEW_CONTAINER_PADDING})`,
            minWidth: PREVIEW_CONTAINER_MIN_WIDTH,
            maxWidth: "100%",
          }}
        >
          <CreativePreview banner={banner} frameless />
        </ModalPanel.Body>

        <ModalPanel.Footer>
          <ButtonV2
            variant="brand"
            onClick={handleCopy}
            data-testid="banner-preview-copy-button"
          >
            <Typo type="labelSM" textColor="white">
              <span className="flex items-center gap-2">
                {copied ? (
                  <CheckIcon width={16} height={16} />
                ) : (
                  <CopyIcon width={16} height={16} />
                )}
                <Typo type="labelSM" textColor="white">
                  {copied ? copiedLabel : copyLabel}
                </Typo>
              </span>
            </Typo>
          </ButtonV2>
        </ModalPanel.Footer>
      </ModalPanel>
    </div>
  );
};

const MetaSeparator = () => (
  <span aria-hidden className="block h-4 w-px bg-stroke-primary" />
);
